import AppLoader from '@nxsys/components/AppLoader';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import { useGetBankList } from '@nxsys/hooks/api/bank';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React, { Ref, useEffect, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AppRateInput from '@nxsys/components/AppRateInput';

type BankFormProps = {
  formRef?: Ref<BankFormRef>;
  onSubmit?: (values: BankValues) => Promise<void>;
  defaultValues?: Partial<BankValues>;
};

export type BankFormRef = {
  submit: () => void;
  reset: () => void;
  setFieldsValue: (values: Partial<BankValues>) => void;
};

export type BankValues = {
  bankName: Option<string> | null;
  accountName: string;
  accountNumber: string;
  sortCode: string;
  isPrimary: boolean;
  IBANReference?: string;
  bankReference?: string;
  BIC?: string;
  orgId?: string;
};

const BankForm: React.FC<BankFormProps> = ({
  formRef,
  onSubmit,
  defaultValues = {},
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const {
    run: fetchBankList,
    loading: isFetchingBankList,
    value: bankListValue,
  } = useGetBankList();
  const bankList = useMemo(() => bankListValue?.data, [bankListValue]);
  const bankNameOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    bankList?.forEach((bank) => {
      if (bank.bankName && !seen.has(bank.bankName)) {
        seen.add(bank.bankName);
        options.push({ label: bank.bankName, value: bank.bankName });
      }
    });
    return options;
  }, [bankList]);
  useEffect(() => {
    fetchBankList({ page: 0, size: 999 });
  }, []);

  const [form] = Form.useForm<BankValues>();
  const watchBankName = Form.useWatch('bankName', form);
  // NX-5464: AIB SEPA files need the employer IBAN/BIC — require them (and
  // the originator ID) at settings time instead of failing on payment day.
  const isAibSelected =
    isIreland &&
    (watchBankName as Option<string> | null)?.value
      ?.toLowerCase()
      .includes('aib');

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
    setFieldsValue(values: Partial<BankValues>) {
      form.setFieldsValue(values);
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    await onSubmit?.(values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={defaultValues}
    >
      {isFetchingBankList && <AppLoader />}
      <Form.Item
        label={messages['common.bankName']}
        name="bankName"
        rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
      >
        <AppSelect
          options={bankNameOptions ?? []}
          placeholder={messages['form.select']}
          loading={isFetchingBankList}
        />
      </Form.Item>
      {/* NX-5841: Ireland pays by IBAN, so Account Name + IBAN are the
          mandatory pair there and sort code / account number become optional.
          AIB keeps its extra BIC + Originator ID requirement (NX-5464). */}
      <Form.Item
        label={messages['common.accountName']}
        name="accountName"
        rules={[
          { required: !!isIreland, message: messages['form.pleaseEnter'] },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.accountNumber8Digits']}
        name="accountNumber"
        rules={[
          {
            required: !isIreland,
            message: messages['form.pleaseEnter'],
          },
          {
            pattern: /^\d{8}$/,
            message: messages['form.accountNumberMustBe8Digits'],
          },
        ]}
      >
        <AppRateInput
          placeholder={messages['form.enter']}
          maxLength={8}
          onChange={(value) => {
            const v = value ?? '';
            if (v.length > 8) {
              form.setFieldValue('accountNumber', v.slice(0, 8));
              form.validateFields(['accountNumber']);
            }
          }}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.sortCode6Digits']}
        name="sortCode"
        rules={[
          {
            required: !isIreland,
            message: messages['form.pleaseEnter'],
          },
          {
            pattern: /^\d{6}$/,
            message: messages['form.sortCodeMustBe6Digits'],
          },
        ]}
        normalize={(value: string) => value.replace(/[^0-9]/g, '')}
      >
        <AppInput placeholder={messages['form.enter']} maxLength={6} />
      </Form.Item>
      {isIreland && (
        <>
          <Form.Item
            label={messages['common.IBANReferenceUpto30Digits']}
            name="IBANReference"
            rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
          >
            <AppInput placeholder={messages['form.enter']} maxLength={30} />
          </Form.Item>
          <Form.Item
            label={messages['common.bankReference']}
            name="bankReference"
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label={messages['common.bic']}
            name="BIC"
            rules={[
              {
                required: !!isAibSelected,
                message: messages['form.pleaseEnter'],
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label="Originator ID"
            name="orgId"
            rules={[
              {
                required: !!isAibSelected,
                message: 'Please enter originator ID',
              },
              {
                pattern: /^IE\d{2}(SCT|MCY|SDD|XXX)\d{6}$/,
                message:
                  'Originator ID must be a valid IEAABBBCCCCCC (e.g. IE94SCT833215)',
              },
            ]}
          >
            <AppInput
              placeholder="Originator ID (e.g. IE94SCT833215)"
              maxLength={13}
            />
          </Form.Item>
        </>
      )}
      <Form.Item label={messages['common.isPrimary']} name="isPrimary">
        <AppSwitch />
      </Form.Item>
    </AppFormContainer>
  );
};

export default BankForm;
